import React from 'react';

import { Formatters, Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { isPhoneSizedScreenOrUserProbablyOnMobile } from '~src/commonInfra/utils/MobileHelper';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { getCommonDatasourceInfo } from '~src/datasource/models/mappers/CommonDatasourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import { TooltipDraftIndicator } from '~src/drafts/components/TooltipDraftIndicator';
import { DetailsTooltipStyles } from '~src/spacesPlatform/components/card/DetailsTooltipStyles';
import { MarkupField } from '~src/spacesPlatform/components/Explorer/widgets/MarkupField';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { BasicField } from '~src/spacesPlatform/components/tooltip/BasicField';
import { EntityLinkProps } from '~src/spacesPlatform/components/tooltip/EntityLink';
import { LocationField } from '~src/spacesPlatform/components/tooltip/LocationField';
import { Name } from '~src/spacesPlatform/components/tooltip/Name';
import { RelatedEntityList } from '~src/spacesPlatform/components/tooltip/RelatedEntityList';
import { Timestamp } from '~src/spacesPlatform/components/tooltip/Timestamp';
import { Tooltip } from '~src/spacesPlatform/components/tooltip/Tooltip';
import {
  ContentTooltipProps,
  getPersonalSpaceLink,
  getProjectLink,
  handleContinueEditingAction,
  TooltipDescriptionTestId,
} from '~src/spacesPlatform/components/tooltip/TooltipHelper';
import { User } from '~src/spacesPlatform/components/tooltip/User';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { Tags } from '~src/tags/components/tooltips/Tags';

import { IWorkbookInfo } from '../models/IWorkbookInfo';

export function WorkbookTooltip(props: ContentTooltipProps): JSX.Element {
  const { content, urls } = props;
  const relatedDatasources = getRelatedDatasourceLinkProps(props.content, props.urls);
  const numSheets = (content as IWorkbookInfo).numSheets;
  const projectLink = getProjectLink((content as IWorkbookInfo).project, urls);
  const personalSpaceLink = getPersonalSpaceLink(urls);
  const locationType = (content as IWorkbookInfo).location?.type;
  const ownerId = content.owner?.id;
  return (
    <Tooltip>
      <Name name={content.name} url={props.contentUrl} />
      <MarkupField markup={props.content.description}
                   testId={TooltipDescriptionTestId}
                   extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]} />
      <dl className={DetailsTooltipStyles.DescriptionList}>
        <Tags tags={props.content.tags} />
        <BasicField name={Messages.sheets()} value={numSheets} />
        <RelatedEntityList label={Messages.column_numDatasources()} list={relatedDatasources} />
        <LocationField inProjectSpace={props.inProjectSpace} inPersonalSpace={props.inPersonalSpace} locationType={locationType}
          projectLink={projectLink} personalSpaceLink={personalSpaceLink} contentOwnerId={ownerId}/>
        <User user={props.content.owner} label={Messages.owner()} urls={urls} />
        <Timestamp label={Messages.modified()} value={props.content.updatedAt} />
        { FeatureFlags.modifiedByUserEnabled && props.content.modifiedBy !== undefined && <User user={props.content.modifiedBy} label={Messages.column_modified_by()} urls={urls} /> }
        {props.draftLastUpdatedTimeStamp && renderDraftIndicator(props.draftLastUpdatedTimeStamp, content)}
      </dl>
    </Tooltip>
  );
}

function getRelatedDatasourceLinkProps(workbook: IContentInfo, urls: SpacesSiteUrls): EntityLinkProps[] {
  if (workbook && workbook.hasOwnProperty('datasources')) {
    const datasources: IDataSourceInfo[] = (workbook as any).datasources;
    return datasources.map(ds => {
      const datasourceDetail = getCommonDatasourceInfo(ds);
      const Icon = DataSources.getIconForDatasourceType(datasourceDetail, 'list');
      const iconNode = <Icon size={18} />;
      return {
        name: ds.name,
        href: urls.workbookDatasourcesUrl(workbook.id),
        iconImagePath: DataSources.getIconSVGForDatasourceType(datasourceDetail, 'tooltip'),
        iconNode
      };
    });
  } else {
    return [];
  }
}

export function renderDraftIndicator(draftLastUpdatedAt: string, workbook: IContentInfo): JSX.Element {
  const LAST_EDITED: string = Formatters.formatLongBestFitRelativeTime(new Date(draftLastUpdatedAt));
  return (
    <TooltipDraftIndicator
      indicatorText={() => Messages.draftLastEditIndicatorText({LAST_EDITED})}
      linkText={Messages.continueEditing}
      handleAction={handleContinueEditingAction(workbook)}
      hideEditingLink={isPhoneSizedScreenOrUserProbablyOnMobile()}
    />
  );
}
